import { Container, Card, Row, Col, Image } from "react-bootstrap";
import AppLoader from 'components/general/AppLoader';
import { useNavigate } from "react-router-dom";
import { hasMemberNumber, membershipBadgeClass } from "utils/acl";
import { getUserInitials, resolveAvatarSrc } from "utils/avatar";

export default function ListUsers({ userList, isLoading, selectedRole = '' }) {
  const navigate = useNavigate();

  const normalizeAclForGrouping = (value) => {
    const normalized = String(value || '').trim().toLowerCase();
    if (normalized === 'officer') return 'Officer';
    if (normalized === 'member') return 'Member';
    if (normalized === 'guest member' || normalized === 'guest_member' || normalized === 'guest-member') return 'Guest Member';
    return 'Guest';
  };

  const handleCardClick = (userId) => {
    navigate(`/profile/${userId}`);
  };

  const renderUserCard = (user) => {
    const userId = String(user?._id || user?.userId || user?.tmAuthUserId || user?.email || '');
    const fullName = `${user?.firstName || ''} ${user?.lastName || ''}`.trim() || 'Unnamed User';
    const membershipType = String(user?.acl || 'Guest');
    const membershipClass = membershipBadgeClass(membershipType);
    const membershipNumber = hasMemberNumber(membershipType)
      ? String(user?.memberNo || '').trim()
      : '';
    const officerRoles = membershipType === 'Officer'
      ? (Array.isArray(user?.clubRoles) ? user.clubRoles : [])
      : [];
    const avatarSrc = resolveAvatarSrc(user?.avatarUrl || user?.avatar);
    const userInitials = getUserInitials(user, 'U');

    return (
      <Col xs={12} md={6} lg={6} className="mb-3" key={userId || fullName}>
        <Card
          className="user-card meetings-mock-card-shell meetings-page-card user-mock-card"
          onClick={() => userId && handleCardClick(userId)}
        >
          <Card.Body className="meetings-mock-card-body">
            <div className="meetings-mock-card">
              <div className="meetings-mock-top">
                <div className="meetings-mock-date-group">
                  <span className={`users-membership-type users-membership-type--${membershipClass}`}>
                    {membershipType}
                  </span>
                  {membershipNumber && (
                    <span className="meetings-mock-number">#{membershipNumber}</span>
                  )}
                </div>
              </div>

              <div className="meetings-mock-avatar">
                {avatarSrc
                  ? <Image src={avatarSrc} alt="User avatar" />
                  : <span className="meetings-mock-avatar-fallback">{userInitials}</span>}
              </div>

              <h3 className="meetings-mock-title">{fullName}</h3>
              <div className="meetings-mock-separator"></div>
              {membershipType === 'Officer' ? (
                <>
                  <div className="meetings-mock-role-label">OFFICER ROLE:</div>
                  <div className="meetings-mock-role-name">
                    {officerRoles.length > 0
                      ? officerRoles.map((role, roleIndex) => (
                        <div key={`${user._id}-role-${roleIndex}`}>{role}</div>
                      ))
                      : <div>Not assigned in controller</div>}
                  </div>
                </>
              ) : (
                <div className="user-role-placeholder" aria-hidden="true"></div>
              )}
            </div>
          </Card.Body>
        </Card>
      </Col>
    );
  };

  const groupedUsers = {
    Officer: userList.filter((user) => normalizeAclForGrouping(user?.acl) === 'Officer'),
    Member: userList.filter((user) => normalizeAclForGrouping(user?.acl) === 'Member'),
    'Guest Member': userList.filter((user) => normalizeAclForGrouping(user?.acl) === 'Guest Member'),
    Guest: userList.filter((user) => normalizeAclForGrouping(user?.acl) === 'Guest')
  };
  const groupedCount = ['Officer', 'Member', 'Guest Member', 'Guest']
    .reduce((sum, group) => sum + groupedUsers[group].length, 0);

  const showGrouped = !selectedRole;

  return (
    <>
      {isLoading ? (
        <AppLoader />
      ) : userList.length === 0 ? (
        <Container>
          <Card className="meetings-page-card mt-3">
            <Card.Body>No users to display.</Card.Body>
          </Card>
        </Container>
      ) : (
        <Container>
          {showGrouped ? (
            <>
              {groupedCount > 0
                ? ['Officer', 'Member', 'Guest Member', 'Guest'].map((group) => (
                  groupedUsers[group].length > 0 ? (
                    <section key={group} className="mb-3 mt-3">
                      <Row className="user-card-list">
                        {groupedUsers[group].map(renderUserCard)}
                      </Row>
                    </section>
                  ) : null
                ))
                : (
                  <Row className="user-card-list">
                    {userList.map(renderUserCard)}
                  </Row>
                )}
            </>
          ) : (
            <Row className="user-card-list">
              {userList.map(renderUserCard)}
            </Row>
          )}
        </Container>
      )}
    </>
  );
}